import React from 'react';

import ShowPageComment from '@buyside/components/presentation/ShowPageComments/ShowPageComment/showPageComment';

import {
  Mentionable,
  IdeaShowPageCommentFragment,
  Maybe,
  useLikeCommentMutation
} from '@interfaces/graphql-typings';

export type ShowPageCommentType = Maybe<
  Omit<IdeaShowPageCommentFragment, 'commentAttachments'> & {
    commentAttachments?: IdeaShowPageCommentFragment['commentAttachments'];
  }
>;

const ShowPageCommentContainer: React.FC<{
  allComments: ShowPageCommentType[];
  authorTag?: string;
  authorId: Maybe<string>;
  authorName: Maybe<string>;
  canCommentAnonymously: boolean;
  canViewUserDetails: boolean;
  canWriteComments: boolean;
  comment: ShowPageCommentType;
  commentableId: string;
  commentableType: 'Idea' | 'WhitePaper';
  currentUserId?: string;
  highlightedCommentId?: Maybe<string>;
  isAdmin: boolean;
  isAllocator: boolean;
  isAuthor: boolean;
  isEmployed: boolean;
  isIdeaAnonymous: boolean;
  isNonBuyside: boolean;
  isIdeaClosed: boolean;
  mentionables: Maybe<Mentionable>[];
  profileImageUrl: Maybe<string>;
}> = ({
  allComments,
  authorTag,
  authorId,
  authorName,
  canCommentAnonymously,
  canViewUserDetails,
  canWriteComments,
  comment,
  commentableId,
  commentableType,
  currentUserId,
  highlightedCommentId,
  isAdmin,
  isAllocator,
  isAuthor,
  isEmployed,
  isIdeaAnonymous,
  isNonBuyside,
  mentionables,
  profileImageUrl
}): Maybe<React.ReactElement> => {
  const [likeComment, { loading: likeLoading }] = useLikeCommentMutation({
    variables: { commentId: comment?.id || '' },
    update(cache, { data }) {
      if (!data?.likeComment?.comment) return;

      const hasLiked = data.likeComment.comment.currentUserHasLiked;

      cache.modify({
        id: cache.identify({ id: data.likeComment.comment.id, __typename: 'Comment' }),
        fields: { currentUserHasLiked: () => hasLiked }
      });
    }
  });

  if (!comment) return null;

  const childComments = allComments.filter(c => c && c.parentId === comment?.id);

  return (
    <>
      <ShowPageComment
        allComments={allComments}
        authorTag={authorTag}
        authorId={authorId}
        authorName={authorName}
        canCommentAnonymously={canCommentAnonymously}
        canViewUserDetails={canViewUserDetails}
        canWriteComments={canWriteComments}
        childComments={childComments}
        comment={comment}
        commentableId={commentableId}
        commentableType={commentableType}
        currentUserId={currentUserId}
        handleLike={likeComment}
        highlightedCommentId={highlightedCommentId}
        isAdmin={isAdmin}
        isAllocator={isAllocator}
        isAuthor={isAuthor}
        isEmployed={isEmployed}
        isIdeaAnonymous={isIdeaAnonymous}
        isNonBuyside={isNonBuyside}
        likeLoading={likeLoading}
        mentionables={mentionables}
        profileImageUrl={profileImageUrl}
      />
    </>
  );
};

export default ShowPageCommentContainer;
